import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import {
  CalendarDate,
  CalendarDateTime,
  ZonedDateTime,
  toCalendarDate,
  today,
  getLocalTimeZone,
} from "@internationalized/date";
import { RangeValue } from "@react-types/shared";
import { Timestamp } from "firebase/firestore";

// Local imports
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import BaseButtonSecondary from "../../components/BaseButtonSecondary";
import BaseInputSelect from "../../components/BaseInputSelect";
import LoadingClipboardAnimation from "../../components/LoadingClipBoardAnimation";
import StyledMessage from "../../components/StyledMessage";
import {
  TransactionStatus,
  getReadableTransactionStatus,
  getTransactionStatusColor,
  ExistingLoyaltyTransaction,
} from "../../models/loyalty-transaction";
import {
  getReadableRewardType,
  ExistingLoyaltyProgramTemplate,
} from "../../models/loyalty-program-template";
import { convertToReadableTimestampDate } from "../../assets/js/convertToReadableTimestamp";
import { DbRead, DbWrite } from "../../database";
import { logger } from "../../logging";
import { useToastMessageStore } from "../../store/toast-messages";
import { createToastMessageID } from "../../utils";
import * as urls from "../../urls";
import { ExistingCustomer } from "../../models/customer";
import { ExistingStiltInvoice } from "../../models/invoice";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import BaseModal from "../../components/BaseModal";
import BaseInputText from "../../components/BaseInputText";
import DateRangePicker from "../../components/DateRangePicker";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";

export type LoyaltyTransactionPlusExtraType = ExistingLoyaltyTransaction & {
  customerName: string;
  invoiceNumber: string | null;
};

interface Props {
  siteKey: string;
}

export default function LoyaltyTransactionsListContainer({ siteKey }: Props) {
  const navigate = useNavigate();
  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );
  const siteKeyDoc = useSiteKeyDocStore((state) => state.siteKeyDoc);

  // Local state
  const [loyaltyTransactions, setLoyaltyTransactions] = useState<
    LoyaltyTransactionPlusExtraType[]
  >([]);
  const [loyaltyPrograms, setLoyaltyPrograms] = useState<
    ExistingLoyaltyProgramTemplate[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [processingTransaction, setProcessingTransaction] = useState<
    string | null
  >(null);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<TransactionStatus | null>(
    null,
  );
  const [programFilter, setProgramFilter] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const pageLimit = 20;

  // MoreRewards member number dialog state
  const [showMemberNumberDialog, setShowMemberNumberDialog] = useState(false);
  const [pendingTransactionForApproval, setPendingTransactionForApproval] =
    useState<string | null>(null);
  const [memberNumberInput, setMemberNumberInput] = useState("");
  const [isSavingMemberNumber, setIsSavingMemberNumber] = useState(false);

  // Date range state - default to last 3 months
  const getDefaultDateRange = (): RangeValue<CalendarDate> => {
    return {
      start: today(getLocalTimeZone()).subtract({ months: 3 }),
      end: today(getLocalTimeZone()),
    };
  };

  const [range, setRange] = useState<RangeValue<CalendarDate>>(
    getDefaultDateRange(),
  );

  // Date range change handler
  const handleDateRangeChange = (
    value: RangeValue<CalendarDate | ZonedDateTime | CalendarDateTime>,
  ) => {
    if (!value.start || !value.end) return;

    const convertedStart = toCalendarDate(value.start);
    const convertedEnd = toCalendarDate(value.end);

    setRange({
      start: convertedStart,
      end: convertedEnd,
    });
  };

  // Convert CalendarDate to Firestore Timestamp
  const calendarDateToTimestamp = (calendarDate: CalendarDate): Timestamp => {
    const date = new Date(
      calendarDate.year,
      calendarDate.month - 1,
      calendarDate.day,
    );
    return Timestamp.fromDate(date);
  };

  // Fetch loyalty programs for filter options
  useEffect(() => {
    let unsubscribePrograms: (() => void) | undefined;

    const setupProgramsSubscription = async () => {
      try {
        unsubscribePrograms = DbRead.loyaltyPrograms.subscribe({
          siteKey,
          onChange: (programs: ExistingLoyaltyProgramTemplate[]) => {
            setLoyaltyPrograms(programs);
          },
          onError: (err) => {
            logger.error("Error in loyalty programs subscription:", err);
          },
        });
      } catch (err) {
        logger.error("Error setting up loyalty programs subscription:", err);
      }
    };

    setupProgramsSubscription();

    return () => {
      if (unsubscribePrograms) {
        unsubscribePrograms();
      }
    };
  }, [siteKey]);

  // Function to enrich transactions with customer and invoice data
  const enrichTransactionsData = async (
    transactions: ExistingLoyaltyTransaction[],
  ): Promise<LoyaltyTransactionPlusExtraType[]> => {
    const enrichedTransactions: LoyaltyTransactionPlusExtraType[] = [];

    for (const transaction of transactions) {
      try {
        // Fetch customer data
        const customer = await DbRead.customers.get(
          siteKey,
          transaction.customerID,
        );
        const customerName =
          customer.name ||
          `${customer.firstName || ""} ${customer.lastName || ""}`.trim() ||
          transaction.customerID;

        // Fetch invoice data
        let invoiceNumber: string | null = null;
        try {
          const invoice = await DbRead.invoices.getSingle(
            siteKey,
            transaction.sourceInvoiceID,
          );
          invoiceNumber = invoice.invoiceNumber;
        } catch (invoiceError) {
          logger.warn(
            `Could not fetch invoice ${transaction.sourceInvoiceID}:`,
            invoiceError,
          );
        }

        enrichedTransactions.push({
          ...transaction,
          customerName,
          invoiceNumber,
        });
      } catch (error) {
        logger.error(`Error enriching transaction ${transaction.id}:`, error);
        // Fallback to transaction ID if customer fetch fails
        enrichedTransactions.push({
          ...transaction,
          customerName: transaction.customerID,
          invoiceNumber: null,
        });
      }
    }

    return enrichedTransactions;
  };

  // Fetch loyalty transactions based on filters and date range
  useEffect(() => {
    let unsubscribeTransactions: (() => void) | undefined;

    const setupTransactionsSubscription = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Convert date range to Firestore timestamps
        const startTimestamp = calendarDateToTimestamp(range.start);
        const endTimestamp = calendarDateToTimestamp(range.end);

        // Use date range subscription with optional status filter
        unsubscribeTransactions =
          DbRead.loyaltyTransactions.subscribeWithDateRange({
            siteKey,
            startDate: startTimestamp,
            endDate: endTimestamp,
            status: statusFilter || undefined,
            onChange: async (transactions: ExistingLoyaltyTransaction[]) => {
              let filteredTransactions = transactions;

              // Apply additional filters
              if (programFilter) {
                filteredTransactions = filteredTransactions.filter(
                  (t) => t.loyaltyProgramID === programFilter,
                );
              }

              const enrichedTransactions =
                await enrichTransactionsData(filteredTransactions);
              setLoyaltyTransactions(enrichedTransactions);
              setIsLoading(false);
            },
            onError: (err) => {
              logger.error("Error in transactions subscription:", err);
              setError("Failed to load transactions");
              setIsLoading(false);
            },
          });
      } catch (err) {
        logger.error("Error setting up transactions subscription:", err);
        setError("Failed to load transactions");
        setIsLoading(false);
      }
    };

    setupTransactionsSubscription();

    return () => {
      if (unsubscribeTransactions) {
        unsubscribeTransactions();
      }
    };
  }, [siteKey, statusFilter, programFilter, range]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleApproveTransaction = async (transactionId: string) => {
    // Check if MoreRewards is configured and if customer has member number
    if (siteKeyDoc?.customizations?.moreRewards) {
      const transaction = loyaltyTransactions.find(
        (t) => t.id === transactionId,
      );
      if (transaction) {
        try {
          const customer = await DbRead.customers.get(
            siteKey,
            transaction.customerID,
          );
          if (!customer.customData?.moreRewardsMemberNumber) {
            // Show dialog to add member number
            setPendingTransactionForApproval(transactionId);
            setMemberNumberInput("");
            setShowMemberNumberDialog(true);
            return;
          }
        } catch (error) {
          logger.error("Error fetching customer for MoreRewards check:", error);
        }
      }
    }

    // Proceed with normal approval
    await approveTransactionDirectly(transactionId);
  };

  const approveTransactionDirectly = async (transactionId: string) => {
    setProcessingTransaction(transactionId);
    try {
      await DbWrite.loyaltyTransactions.approve({
        siteKey,
        transactionID: transactionId,
        processingNote: "Approved via admin interface",
        sendEmail: true,
      });
      addToastMessage({
        id: createToastMessageID(),
        message: "Transaction approved successfully",
        type: "success",
        dialog: false,
      });
      logger.info("Transaction approved", { siteKey, transactionId });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to approve transaction";
      addToastMessage({
        id: createToastMessageID(),
        message: errorMessage,
        type: "error",
        dialog: true,
      });
      logger.error("Error approving transaction:", error);
    } finally {
      setProcessingTransaction(null);
    }
  };

  const handleSaveMemberNumber = async () => {
    if (!pendingTransactionForApproval || !memberNumberInput.trim()) {
      return;
    }

    setIsSavingMemberNumber(true);
    try {
      const transaction = loyaltyTransactions.find(
        (t) => t.id === pendingTransactionForApproval,
      );
      if (transaction) {
        const customer = await DbRead.customers.get(
          siteKey,
          transaction.customerID,
        );

        // Update customer with MoreRewards member number
        await DbWrite.customers.update({
          ...customer,
          customData: {
            ...customer.customData,
            moreRewardsMemberNumber: memberNumberInput.trim(),
          },
        });

        setShowMemberNumberDialog(false);
        addToastMessage({
          id: createToastMessageID(),
          message: "MoreRewards member number added successfully",
          type: "success",
          dialog: false,
        });

        // Now approve the transaction
        await approveTransactionDirectly(pendingTransactionForApproval);
      }
    } catch (error) {
      logger.error("Error saving MoreRewards member number:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Failed to save MoreRewards member number",
        type: "error",
        dialog: true,
      });
    } finally {
      setIsSavingMemberNumber(false);
      setPendingTransactionForApproval(null);
      setMemberNumberInput("");
    }
  };

  const handleCancelMemberNumberDialog = () => {
    setShowMemberNumberDialog(false);
    setPendingTransactionForApproval(null);
    setMemberNumberInput("");
  };

  const handleRejectTransaction = async (transactionId: string) => {
    setProcessingTransaction(transactionId);
    try {
      await DbWrite.loyaltyTransactions.reject({
        siteKey,
        transactionID: transactionId,
        processingNote: "Rejected via admin interface",
      });
      addToastMessage({
        id: createToastMessageID(),
        message: "Transaction rejected successfully",
        type: "success",
        dialog: false,
      });
      logger.info("Transaction rejected", { siteKey, transactionId });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to reject transaction";
      addToastMessage({
        id: createToastMessageID(),
        message: errorMessage,
        type: "error",
        dialog: true,
      });
      logger.error("Error rejecting transaction:", error);
    } finally {
      setProcessingTransaction(null);
    }
  };

  const handleClearFilters = () => {
    setStatusFilter(null);
    setProgramFilter(null);
    setRange(getDefaultDateRange());
    setCurrentPage(1);
  };

  const handleNavigateToDashboard = () => {
    navigate(urls.LOYALTY_DASHBOARD_URL);
  };

  const getCustomerDisplayName = (
    transaction: LoyaltyTransactionPlusExtraType,
  ): string => {
    return transaction.customerName;
  };

  const getProgramDisplayName = (
    transaction: LoyaltyTransactionPlusExtraType,
  ): string => {
    const program = loyaltyPrograms.find(
      (p: ExistingLoyaltyProgramTemplate) =>
        p.id === transaction.loyaltyProgramID,
    );
    return program?.name || transaction.loyaltyProgramID;
  };

  if (isLoading && loyaltyTransactions.length === 0) {
    return (
      <div className="flex h-full flex-auto flex-col items-center justify-center">
        <LoadingClipboardAnimation />
        <p className="mt-4 text-gray-600">Loading loyalty transactions...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Loyalty Transactions
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            View and manage customer loyalty reward transactions
          </p>
        </div>
        <div className="flex gap-3">
          <BaseButtonSecondary onClick={handleNavigateToDashboard}>
            <EyeIcon className="h-4 w-4" />
            Dashboard
          </BaseButtonSecondary>
          <BaseButtonSecondary onClick={() => setShowFilters(!showFilters)}>
            <FunnelIcon className="h-4 w-4" />
            Filters
          </BaseButtonSecondary>
        </div>
      </div>

      {/* Error Messages */}
      {error && (
        <StyledMessage type="error">{{ message: error }}</StyledMessage>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          {/* Date Range Filter */}
          <div className="mb-6">
            <h4 className="mb-2 text-sm font-medium text-gray-900">
              Date Range
            </h4>
            <DateRangePicker value={range} onChange={handleDateRangeChange} />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <BaseInputSelect
              text="Status"
              value={statusFilter || ""}
              onChange={(e) =>
                setStatusFilter(
                  e.target.value ? (e.target.value as TransactionStatus) : null,
                )
              }
              admin
            >
              <option value="">All Status</option>
              {Object.values(TransactionStatus).map((status) => (
                <option key={status} value={status}>
                  {getReadableTransactionStatus(status)}
                </option>
              ))}
            </BaseInputSelect>

            <BaseInputSelect
              text="Program"
              value={programFilter || ""}
              onChange={(e) => setProgramFilter(e.target.value || null)}
              admin
            >
              <option value="">All Programs</option>
              {loyaltyPrograms.map(
                (program: ExistingLoyaltyProgramTemplate) => (
                  <option key={program.id} value={program.id}>
                    {program.name}
                  </option>
                ),
              )}
            </BaseInputSelect>
          </div>

          <div className="mt-4 flex justify-end">
            <BaseButtonSecondary onClick={handleClearFilters}>
              Clear Filters
            </BaseButtonSecondary>
          </div>
        </div>
      )}

      {/* Date Range Display */}
      <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-blue-50 px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-blue-900">Date Range:</span>
          <span className="text-sm text-blue-800">
            {range.start.toString()} to {range.end.toString()}
          </span>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="text-sm text-blue-600 hover:text-blue-500"
        >
          Change Date Range
        </button>
      </div>

      {/* Transactions Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-medium text-gray-900">
            Transactions ({loyaltyTransactions.length})
          </h3>
        </div>

        {loyaltyTransactions.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500">No loyalty transactions found.</p>
            {(statusFilter || programFilter) && (
              <p className="mt-2 text-sm text-gray-400">
                Try adjusting your filters to see more results.
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Program
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Reward
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {loyaltyTransactions.map(
                  (transaction: LoyaltyTransactionPlusExtraType) => (
                    <tr key={transaction.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                        {convertToReadableTimestampDate(transaction.earnedDate)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {getCustomerDisplayName(transaction)}
                        </div>
                        <div className="text-sm text-gray-500">
                          Invoice:{" "}
                          {transaction.invoiceNumber ||
                            transaction.sourceInvoiceID}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                        {getProgramDisplayName(transaction)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {getReadableRewardType(transaction.rewardType)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {transaction.pointsEarned
                            ? `${transaction.pointsEarned} pts`
                            : transaction.discountAmount
                              ? `$${transaction.discountAmount}`
                              : `${transaction.rewardValue}`}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                        ${transaction.sourceAmount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getTransactionStatusColor(transaction.status)}`}
                          >
                            {getReadableTransactionStatus(transaction.status)}
                          </span>
                        </div>
                        {transaction.processingNote && (
                          <div className="mt-1 max-w-48 text-xs italic text-gray-500">
                            Note: {transaction.processingNote}
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                        {transaction.status === TransactionStatus.PENDING && (
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() =>
                                handleApproveTransaction(transaction.id)
                              }
                              disabled={
                                processingTransaction === transaction.id
                              }
                              className="inline-flex items-center rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-200 disabled:opacity-50"
                            >
                              <CheckCircleIcon className="mr-1 h-3 w-3" />
                              Approve
                            </button>
                            <button
                              onClick={() =>
                                handleRejectTransaction(transaction.id)
                              }
                              disabled={
                                processingTransaction === transaction.id
                              }
                              className="inline-flex items-center rounded bg-red-100 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-200 disabled:opacity-50"
                            >
                              <XCircleIcon className="mr-1 h-3 w-3" />
                              Reject
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {loyaltyTransactions.length > 0 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
            <div className="text-sm text-gray-700">
              Page {currentPage} • {loyaltyTransactions.length} transactions
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ChevronLeftIcon className="mr-1 h-4 w-4" />
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!hasMore || isLoading}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
                <ChevronRightIcon className="ml-1 h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MoreRewards Member Number Dialog */}
      <BaseModal
        open={showMemberNumberDialog}
        closeModal={handleCancelMemberNumberDialog}
        title={<></>}
        parentDivStyles="text-left max-w-md"
      >
        <div className="mb-4 flex w-full items-center justify-between rounded-t-lg bg-primary p-8 text-left text-white">
          <div className="inline-flex items-center gap-3">
            <img
              src="/moreRewards_logo.svg"
              alt="MoreRewards"
              className="h-8"
            />
            <h1 className="text-xl font-medium">Add Member Number</h1>
          </div>
          <button type="button" onClick={handleCancelMemberNumberDialog}>
            <XMarkIcon
              aria-label="close member number dialog"
              className="h-6 text-white"
            />
          </button>
        </div>

        <div className="space-y-4 p-6">
          <p className="text-sm text-gray-600">
            This customer needs a MoreRewards member number to process loyalty
            rewards. Please add their member number to continue with approval.
          </p>

          <div>
            <div className="mb-2 flex items-center gap-2">
              <img
                src="/moreRewards_logo.svg"
                alt="MoreRewards"
                className="h-4"
              />
              <span className="text-sm font-medium text-gray-700">
                Member Number
              </span>
            </div>
            <BaseInputText
              text=""
              inputName="memberNumber"
              admin={true}
              required={true}
              value={memberNumberInput}
              onChange={(e) => setMemberNumberInput(e.target.value)}
              placeholder="Enter member number"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <BaseButtonSecondary
              onClick={handleCancelMemberNumberDialog}
              disabled={isSavingMemberNumber}
            >
              Cancel
            </BaseButtonSecondary>
            <BaseButtonPrimary
              onClick={handleSaveMemberNumber}
              disabled={!memberNumberInput.trim() || isSavingMemberNumber}
              isBusy={isSavingMemberNumber}
              busyText="Saving..."
            >
              Save & Approve Transaction
            </BaseButtonPrimary>
          </div>
        </div>
      </BaseModal>
    </div>
  );
}
